/**
 * The browser's signal that our API refused the signed-in person (tenant-isolation WP1
 * addendum, owner's point P2). `api-client.ts` announces every 401/403 from an `/api/*` route;
 * `useSessionMonitor` listens and decides whether the person's access has ended:
 *
 *   403 `no_membership`    their membership in this operator ended     → signed out
 *   403 `account_disabled` their account is disabled                   → signed out
 *   401 on a token that should still be good → one session refresh; if that fails too (the
 *        off-boarding trigger deletes every refresh token, and hasura-auth refuses a disabled
 *        account at `/token/verify` and `/token`) → signed out
 *
 * Every other refusal (403 forbidden, `password_change_required`, …) is left to the screen that
 * made the call. A 503 `access_check_unavailable` (the server could not check access — an outage,
 * WP1 review F2) is not a refusal at all: it is never announced here and never signs anyone out. Nothing here is a security decision — the server has already refused; this only
 * stops the dashboard pretending the person is still in.
 */

export const ACCESS_REFUSED_EVENT = "auth:access-refused";

export interface AccessRefusedDetail {
  status: 401 | 403;
  /** The body's `code`, when the route gave one. */
  code: string | null;
}

/** The 403 codes that mean "this person no longer has access at all". */
export const ACCESS_ENDED_CODES: ReadonlySet<string> = new Set([
  "no_membership",
  "account_disabled",
]);

export function announceAccessRefused(detail: AccessRefusedDetail): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<AccessRefusedDetail>(ACCESS_REFUSED_EVENT, { detail }),
  );
}

/** Why the login page is being shown; read from `?reason=` by the login form. */
export type LoginReason =
  | "account-disabled"
  | "access-ended"
  | "session-expired"
  | "password-changed";
